/**
 * CaseHistoryTab — History & Audit Trail
 *
 * Renders:
 *  - Step Timeline: vertical timeline of step_history entries
 *  - Audit Log: paginated table of all AuditEntry records for the case
 */
import React, { useState, useEffect } from "react";
import { api } from "../services/api.js";

const STEP_LABELS: Record<string, string> = {
  step_1_initiation: "Case Initiation",
  step_2_field_dispatch: "Field Seller Dispatch",
  step_3_field_verification: "Field Verification",
  step_4_hub_dispatch: "Hub Dispatch",
  step_5_hub_witness: "Hub Witness",
  step_6_hub_processing: "Hub Processing",
  step_7_expert_dispatch: "Expert Dispatch",
  step_8_expert_valuation: "Expert Valuation",
  step_9_cataloging: "Cataloging",
  step_10_auction_approval: "Auction Approval",
  step_11_market_listing: "Market Listing",
  step_12_hub_inspection: "Hub Inspection",
  step_13_bidding_approval: "Bidding Approval",
  step_14_market_bidding: "Live Bidding",
  step_15_market_closed: "Auction Closed",
  step_16_winner_approval: "Winner Approval",
  step_17_winner_posted: "Winner Posted",
  step_18_settlement: "Settlement",
};

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function duration(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const STATUS_COLORS: Record<string, string> = {
  completed: "#10b981",
  active: "#6366f1",
  timeout: "#f59e0b",
  failed: "#ef4444",
};

const EVENT_ICONS: Record<string, string> = {
  "case:created": "📁",
  "flag:updated": "🚩",
  "step:advanced": "➡️",
  "step:activated": "▶️",
  "case:closed": "✅",
  "dispatch:created": "📡",
  "dispatch:accepted": "🤝",
  "dispatch:declined": "❌",
  "dispatch:timeout": "⏱️",
  "module:submitted": "📤",
  "case:reset": "↩️",
  "step:sla_timeout": "⚠️",
  "platform:listing_published": "🏷️",
  "platform:bidding_started": "🔨",
  "platform:auction_close_requested": "🔔",
  "platform:winner_posted": "🏆",
};

// ─── Step Timeline ────────────────────────────────────────────────────────────

interface StepEntry {
  step_id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  actor_id: string | null;
}

function StepTimeline({ caseId }: { caseId: string }): React.ReactElement {
  const [entries, setEntries] = useState<StepEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api.cases.history(caseId)
      .then((r) => setEntries(r.data.history ?? []))
      .catch(() => {/* handled by empty state */})
      .finally(() => setLoading(false));
  }, [caseId]);

  if (loading) return <p style={{ color: "var(--muted)", fontSize: 14 }}>Loading timeline…</p>;
  if (entries.length === 0) return <p style={{ color: "var(--muted)", fontSize: 14 }}>No step history recorded yet.</p>;

  return (
    <div style={{ position: "relative", paddingLeft: 32 }}>
      {/* Vertical line */}
      <div style={{ position: "absolute", left: 10, top: 12, bottom: 12, width: 2, background: "var(--border)" }} />

      {entries.map((e, idx) => {
        const color = STATUS_COLORS[e.status] ?? "#6366f1";
        const label = STEP_LABELS[e.step_id] ?? e.step_id.replace(/_/g, " ");
        const dur = duration(e.started_at, e.completed_at);
        return (
          <div key={`${e.step_id}-${idx}`} style={{ display: "flex", gap: 14, marginBottom: 20, position: "relative" }}>
            {/* Node dot */}
            <div style={{
              position: "absolute", left: -32, top: 2,
              width: 20, height: 20, borderRadius: "50%",
              background: color, display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 0 0 3px ${color}30`,
              fontSize: 10, color: "#fff", fontWeight: 900, flexShrink: 0,
            }}>
              {idx + 1}
            </div>

            <div style={{ background: "var(--bg)", border: `1px solid ${color}30`, borderLeft: `3px solid ${color}`, borderRadius: "var(--radius-sm)", padding: "10px 14px", flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: "var(--ink)" }}>{label}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--muted)" }}>
                    {e.actor_id ? `By: ${e.actor_id}` : "System"}
                  </p>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                  padding: "3px 8px", borderRadius: 999,
                  background: `${color}18`, color, border: `1px solid ${color}40`,
                }}>
                  {e.status}
                </span>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
                <span>Started: {fmt(e.started_at)}</span>
                {e.completed_at && <span>Completed: {fmt(e.completed_at)}</span>}
                {dur !== "—" && <span>Duration: <strong style={{ color: "var(--ink)" }}>{dur}</strong></span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

interface AuditEntry {
  event_type: string;
  case_id?: string;
  actor_id?: string;
  application?: string;
  details?: Record<string, unknown>;
  occurred_at?: string;
  _id?: string;
}

const PAGE_SIZE = 15;

function AuditLog({ caseId }: { caseId: string }): React.ReactElement {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    void api.cases.auditTrail(caseId)
      .then((r) => setEntries(r.data as AuditEntry[]))
      .catch(() => {/* handled */})
      .finally(() => setLoading(false));
  }, [caseId]);

  if (loading) return <p style={{ color: "var(--muted)", fontSize: 14 }}>Loading audit log…</p>;
  if (entries.length === 0) return <p style={{ color: "var(--muted)", fontSize: 14 }}>No audit events recorded yet.</p>;

  const totalPages = Math.ceil(entries.length / PAGE_SIZE);
  const pageEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>{entries.length} events total</p>
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              style={{ padding: "4px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "var(--panel)", cursor: page === 0 ? "not-allowed" : "pointer", fontSize: 12 }}>
              ← Prev
            </button>
            <span style={{ fontSize: 12, color: "var(--muted)", padding: "4px 8px" }}>{page + 1} / {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              style={{ padding: "4px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "var(--panel)", cursor: page >= totalPages - 1 ? "not-allowed" : "pointer", fontSize: 12 }}>
              Next →
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {pageEntries.map((e, idx) => {
          const key = e._id ?? `${idx}`;
          const icon = EVENT_ICONS[e.event_type] ?? "📋";
          const isOpen = expanded === key;
          return (
            <div key={key}
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
              <button
                onClick={() => setExpanded(isOpen ? null : key)}
                style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", flex: 1 }}>
                  {e.event_type.replace(/:/g, " › ")}
                </span>
                {e.application && (
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", border: "1px solid var(--border)", padding: "2px 6px", borderRadius: 999 }}>
                    {e.application}
                  </span>
                )}
                <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap", marginLeft: 8 }}>{fmt(e.occurred_at)}</span>
                <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: 4 }}>{isOpen ? "▲" : "▼"}</span>
              </button>
              {isOpen && (
                <div style={{ padding: "0 14px 12px", borderTop: "1px solid var(--border)" }}>
                  {e.actor_id && (
                    <p style={{ margin: "8px 0 4px", fontSize: 12, color: "var(--muted)" }}>
                      Actor: <strong style={{ color: "var(--ink)" }}>{e.actor_id}</strong>
                    </p>
                  )}
                  {e.details && Object.keys(e.details).length > 0 && (
                    <pre style={{ margin: "6px 0 0", fontSize: 11, background: "var(--panel)", padding: "8px 10px", borderRadius: "var(--radius-sm)", overflowX: "auto", color: "var(--ink)" }}>
                      {JSON.stringify(e.details, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

export function CaseHistoryTab({ caseId }: { caseId: string }): React.ReactElement {
  const [section, setSection] = useState<"timeline" | "audit">("timeline");

  const tab = (id: "timeline" | "audit", label: string) => (
    <button key={id} onClick={() => setSection(id)}
      style={{
        padding: "8px 16px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700,
        background: section === id ? "var(--brand)" : "transparent",
        color: section === id ? "#fff" : "var(--muted)",
        borderRadius: "var(--radius-sm)",
      }}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, padding: "4px", background: "var(--bg)", borderRadius: "var(--radius-sm)", width: "fit-content" }}>
        {tab("timeline", "📋 Step Timeline")}
        {tab("audit", "🔍 Audit Log")}
      </div>

      {section === "timeline" && <StepTimeline caseId={caseId} />}
      {section === "audit" && <AuditLog caseId={caseId} />}
    </div>
  );
}
